// AI Migration Center — import existing workforce data with Dana. Upload a CSV,
// let Dana map messy (English/Swahili) columns to canonical fields with confidence,
// review/override, validate (dry-run), approve, then execute. migration-service
// orchestrates; the actual write goes through employee-service /import.
import { formatDate } from '@/lib/orgFormat';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Database, Upload, Sparkles, Loader2, CheckCircle2, AlertTriangle, ChevronRight,
  ArrowLeft, Wand2, ShieldCheck, Play, RotateCcw,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import {
  listMigrations, createMigration, uploadSource, uploadSourceBinary, connectSource, getMigration, getRows,
  suggestMapping, setMappings, validateMigration, approveMigration, executeMigration, rollbackMigration,
  getIntelligence, getIntelligenceDashboard, clearMigrationHistory, getCreatableCompanies, listCompanyNames,
  MIGRATION_TARGETS, fieldsForTarget, type MigrationJob, type Mapping, type MigrationRow, type ImportResult, type MigrationIntelligence, type MigrationDashboard, type RollbackResult,
} from '@/services/migrationService';
import { Brain, Trash2, BarChart3, ChevronDown, ChevronUp, TrendingUp, Layers, Camera, Building2 } from 'lucide-react';
import { useHasPermission } from '@/rbac/useRbac';
import { getApiErrorMessage } from '@/lib/apiError';
import { SnapshotList, SnapshotView } from './SnapshotPanel';
import { AttendanceIdentityCard, AttendanceSourceCard } from './AttendanceSourcePanel';
import { GROUP_WIDE, companyChoices, companyLabel, legalEntityIdFor, type CompanyChoices } from './migrationCompany';

const fieldCls = 'rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

const statusColor: Record<string, string> = {
  draft: 'bg-slate-500/15 text-slate-600 dark:text-slate-300',
  staged: 'bg-blue-500/15 text-blue-600 dark:text-blue-300',
  mapped: 'bg-violet-500/15 text-violet-600 dark:text-violet-300',
  validated: 'bg-amber-500/15 text-amber-600 dark:text-amber-300',
  approved: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300',
  executing: 'bg-amber-500/15 text-amber-600 dark:text-amber-300',
  completed: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300',
  failed: 'bg-red-500/15 text-red-600 dark:text-red-300',
  rolled_back: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
};
const StatusChip = ({ s }: { s: string }) => (
  <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${statusColor[s] || 'bg-muted'}`}>{s.replace(/_/g, ' ')}</span>
);

// Which company a job is for. Untagged = group-wide: only people who reach
// every company can see it, so saying so is the point.
export const CompanyChip: React.FC<{ id?: string | null; names: Map<string, string> }> = ({ id, names }) => (
  <span data-testid="job-company" className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
    <Building2 className="h-3 w-3" aria-hidden />
    {id ? companyLabel(id, names) : 'All companies (group-wide)'}
  </span>
);

function confChip(level: string, conf: number) {
  const map: Record<string, string> = {
    high: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300',
    review: 'bg-amber-500/15 text-amber-600 dark:text-amber-300',
    manual: 'bg-red-500/15 text-red-600 dark:text-red-300',
  };
  const label: Record<string, string> = { high: 'High', review: 'Review', manual: 'Manual' };
  return <span className={`rounded px-1.5 py-0.5 text-xs ${map[level] || 'bg-muted'}`}>{Math.round(conf)}% · {label[level] || level}</span>;
}

export const MigrationCenterPage: React.FC<{ defaultTarget?: string }> = ({ defaultTarget = 'employees' }) => {
  const canRead = useHasPermission('migration:read');
  const [jobs, setJobs] = useState<MigrationJob[]>([]);
  // A failed list is not an empty one: "No migrations yet." must never stand in
  // for "could not load".
  const [listError, setListError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [openSnapId, setOpenSnapId] = useState<string | null>(null);
  const payroll = defaultTarget === 'payroll';
  // Company names for the job chips (labels only; never decides access).
  const [companyNames, setCompanyNames] = useState<Map<string, string>>(new Map());
  useEffect(() => { if (canRead) listCompanyNames().then(setCompanyNames).catch(() => {}); }, [canRead]);

  const reload = () => listMigrations()
    .then((j) => { setJobs(j); setListError(null); })
    .catch((e) => { setJobs([]); setListError(getApiErrorMessage(e, 'Could not load migrations.')); });
  useEffect(() => { if (canRead) reload(); }, [canRead]);

  if (!canRead) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        You don't have permission to view the Migration Center.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-primary-500/10 p-2 text-primary-600 dark:text-primary-400"><Database className="h-6 w-6" /></div>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{payroll ? 'Import Salaries' : 'AI Migration Center'}</h1>
          <p className="text-sm text-muted-foreground">{payroll
            ? 'Bulk-import base salaries — Dana maps your columns, you review, then go live. Employees must already exist in payroll.'
            : 'Import your existing workforce — Dana maps your columns, you review, then go live.'}</p>
        </div>
      </div>

      {openSnapId
        ? <SnapshotView id={openSnapId} onBack={() => { setOpenSnapId(null); reload(); }} onOpenJob={(id) => { setOpenSnapId(null); setOpenId(id); }} />
        : openId
          ? <JobView id={openId} companyNames={companyNames} onBack={() => { setOpenId(null); reload(); }} onOpenSnapshot={setOpenSnapId} />
          : (
            <div className="space-y-4">
              {!payroll && <SnapshotList onOpen={setOpenSnapId} />}
              <JobList jobs={jobs} listError={listError} companyNames={companyNames} defaultTarget={defaultTarget} onOpen={setOpenId} onCreated={(id) => { reload(); setOpenId(id); }} />
            </div>
          )}
    </div>
  );
};

// Targets whose imports can be rolled back, with their confirm + hint copy. A
// target absent here (employees) shows no rollback button.
const ROLLBACK_COPY: Record<string, { confirm: string; hint: string }> = {
  employees: {
    confirm: 'Roll back this import? It will delete the employees this import added and restore the previous details for any it changed. Downstream services (payroll, leave, benefits) are notified to drop the deleted employees.',
    hint: 'Removes the employees this import added and restores any it changed.',
  },
  payroll: {
    confirm: 'Roll back this import? It will delete the salaries this import set and restore the previous values for any employees it changed.',
    hint: 'Reverses the compensation this import wrote.',
  },
  payroll_history: {
    confirm: 'Roll back this import? It will remove the historical payroll runs this import created. Live payroll is never touched — historical runs cannot enter the payment pipeline.',
    hint: 'Removes the migrated historical payroll runs (read-only records).',
  },
  leave: {
    confirm: 'Roll back this import? It will delete the leave balances this import created and restore the previous values for any it changed.',
    hint: 'Reverses the leave balances this import wrote.',
  },
  benefits: {
    confirm: 'Roll back this import? It will remove the enrollments this import created and restore the previous coverage for any it changed.',
    hint: 'Reverses the benefit enrollments this import wrote.',
  },
};

// ----------------------------------------------------------- intelligence
const ORIGIN_META: Record<string, { label: string; color: string }> = {
  tenant: { label: 'Your memory', color: 'bg-emerald-500' },
  ai: { label: 'Dana (AI)', color: 'bg-violet-500' },
  global: { label: 'Shared patterns', color: 'bg-sky-500' },
  alias: { label: 'Known aliases', color: 'bg-amber-500' },
  manual: { label: 'Manual', color: 'bg-zinc-400' },
};

const IntelligencePanel: React.FC = () => {
  const confirm = useConfirm();
  const [s, setS] = useState<MigrationIntelligence | null>(null);
  const [dash, setDash] = useState<MigrationDashboard | null>(null);
  const [open, setOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const load = () => getIntelligence().then(setS).catch(() => setS(null));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (open && !dash) getIntelligenceDashboard().then(setDash).catch(() => setDash(null)); }, [open, dash]);
  if (!s || (s.learned_mappings === 0 && s.global_patterns === 0)) return null;
  const stat = (label: string, value: React.ReactNode) => (
    <div className="rounded-lg border border-border p-3"><div className="text-xs text-muted-foreground">{label}</div><div className="text-lg font-semibold text-foreground">{value}</div></div>
  );
  const clear = async () => {
    if (!(await confirm({ title: 'Clear migration memory', message: 'Clear your private migration memory? Dana will forget this company’s learned mappings.', confirmLabel: 'Clear', danger: true }))) return;
    setClearing(true);
    try { await clearMigrationHistory(); setDash(null); load(); } finally { setClearing(false); }
  };
  return (
    <Card><CardContent className="p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground flex items-center gap-2"><Brain className="h-4 w-4 text-primary-500" /> Migration intelligence</h2>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setOpen((o) => !o)}>
            <BarChart3 className="mr-2 h-4 w-4" /> {open ? 'Hide' : 'View'} dashboard {open ? <ChevronUp className="ml-1 h-3.5 w-3.5" /> : <ChevronDown className="ml-1 h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={clear} disabled={clearing}>
            {clearing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Clear memory
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stat('Learned mappings', s.learned_mappings)}
        {stat('Avg AI confidence', `${Math.round(s.avg_confidence)}%`)}
        {stat('Your corrections', s.admin_corrections)}
        {stat('Global patterns', s.global_patterns)}
      </div>
      {s.top_patterns?.length > 0 && (
        <div className="text-xs text-muted-foreground">
          Dana remembers: {s.top_patterns.slice(0, 5).map((p) => `${p.source}→${p.target}`).join(' · ')}
        </div>
      )}
      {open && <IntelligenceDashboard d={dash} />}
    </CardContent></Card>
  );
};

// Full learning dashboard, revealed under the compact panel.
const IntelligenceDashboard: React.FC<{ d: MigrationDashboard | null }> = ({ d }) => {
  if (!d) return <div className="flex items-center gap-2 pt-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading dashboard…</div>;
  const trend = d.confidence_trend;
  const maxMapped = Math.max(1, ...trend.map((t) => t.mapped));
  return (
    <div className="space-y-5 border-t border-border pt-4">
      {/* top row: extra headline tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-border p-3"><div className="text-xs text-muted-foreground">Imports run</div><div className="text-lg font-semibold text-foreground">{d.total_jobs} <span className="text-xs font-normal text-muted-foreground">({d.completed_jobs} completed)</span></div></div>
        <div className="rounded-lg border border-border p-3"><div className="text-xs text-muted-foreground">Auto-mapped (high confidence)</div><div className="text-lg font-semibold text-foreground">{Math.round(d.auto_map_rate)}%</div></div>
        <div className="rounded-lg border border-border p-3"><div className="text-xs text-muted-foreground">Headers Dana struggles with</div><div className="text-lg font-semibold text-foreground">{d.weak_headers.length}</div></div>
      </div>

      {/* confidence trend */}
      <div>
        <h4 className="mb-2 text-xs font-semibold text-foreground flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5 text-primary-500" /> Confidence over time</h4>
        {trend.length === 0 ? <p className="text-xs text-muted-foreground">No suggestions yet.</p> : (
          <div className="flex items-end gap-1.5 h-24">
            {trend.map((t, i) => (
              <div key={i} className="group relative flex-1 flex flex-col items-center justify-end">
                <div className="w-full rounded-t bg-primary-500/70 hover:bg-primary-500 transition-all" style={{ height: `${Math.max(4, t.avg_confidence)}%` }} title={`${t.date}: ${Math.round(t.avg_confidence)}% over ${t.mapped} columns`} />
                <div className="absolute -top-5 hidden group-hover:block text-[10px] font-medium text-foreground">{Math.round(t.avg_confidence)}%</div>
              </div>
            ))}
          </div>
        )}
        {trend.length > 0 && <div className="mt-1 flex justify-between text-[10px] text-muted-foreground"><span>{trend[0].date}</span><span>{trend[trend.length - 1].date}</span></div>}
      </div>

      {/* resolution mix */}
      <div>
        <h4 className="mb-2 text-xs font-semibold text-foreground flex items-center gap-1.5"><Layers className="h-3.5 w-3.5 text-primary-500" /> How columns get mapped</h4>
        {d.resolution_mix.length === 0 ? <p className="text-xs text-muted-foreground">No mappings yet.</p> : (
          <>
            <div className="flex h-3 w-full overflow-hidden rounded-full">
              {d.resolution_mix.map((o) => (
                <div key={o.origin} className={ORIGIN_META[o.origin]?.color || 'bg-zinc-400'} style={{ width: `${o.pct}%` }} title={`${ORIGIN_META[o.origin]?.label || o.origin}: ${o.count} (${Math.round(o.pct)}%)`} />
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {d.resolution_mix.map((o) => (
                <span key={o.origin} className="flex items-center gap-1.5">
                  <span className={`h-2.5 w-2.5 rounded-sm ${ORIGIN_META[o.origin]?.color || 'bg-zinc-400'}`} />
                  {ORIGIN_META[o.origin]?.label || o.origin} <span className="text-foreground font-medium">{Math.round(o.pct)}%</span>
                </span>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">The more “Your memory” grows, the less Dana has to re-think — that’s the learning paying off.</p>
          </>
        )}
      </div>

      {/* per-target coverage */}
      {d.target_coverage.length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-semibold text-foreground">Coverage by destination</h4>
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-xs">
              <thead><tr className="bg-muted/50 text-left text-muted-foreground"><th className="px-3 py-1.5 font-medium">Destination</th><th className="px-3 py-1.5 font-medium text-right">Columns mapped</th><th className="px-3 py-1.5 font-medium text-right">Avg confidence</th><th className="px-3 py-1.5 font-medium text-right">Corrections</th></tr></thead>
              <tbody>
                {d.target_coverage.map((t) => (
                  <tr key={t.target} className="border-t border-border">
                    <td className="px-3 py-1.5 font-medium capitalize text-foreground">{t.target}</td>
                    <td className="px-3 py-1.5 text-right">{t.mappings}</td>
                    <td className="px-3 py-1.5 text-right">{Math.round(t.avg_confidence)}%</td>
                    <td className="px-3 py-1.5 text-right">{t.corrections}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* corrections + weak headers */}
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <h4 className="mb-2 text-xs font-semibold text-foreground">Recent corrections</h4>
          {d.corrections.length === 0 ? <p className="text-xs text-muted-foreground">None — you’ve accepted every suggestion so far. 🎯</p> : (
            <ul className="space-y-1.5 text-xs">
              {d.corrections.map((c, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <span className="font-medium text-foreground">{c.source}</span>
                  <span className="text-muted-foreground">{c.ai_target || '—'}</span>
                  <ChevronRight className="h-3 w-3 text-muted-foreground" />
                  <span className="text-emerald-600 dark:text-emerald-400">{c.final_target || 'ignored'}</span>
                  <span className="ml-auto text-muted-foreground">{c.when}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h4 className="mb-2 text-xs font-semibold text-foreground">Headers needing attention</h4>
          {d.weak_headers.length === 0 ? <p className="text-xs text-muted-foreground">None — Dana’s mapping your columns cleanly.</p> : (
            <div className="flex flex-wrap gap-1.5">
              {d.weak_headers.map((w) => (
                <span key={w.source} className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-700 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300" title={`${w.corrections} of ${w.seen} times needed a manual fix`}>
                  {w.source} <span className="opacity-70">({w.corrections}/{w.seen})</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------- list
const JobList: React.FC<{ jobs: MigrationJob[]; listError?: string | null; companyNames: Map<string, string>; defaultTarget?: string; onOpen: (id: string) => void; onCreated: (id: string) => void }> = ({ jobs, listError, companyNames, defaultTarget = 'employees', onOpen, onCreated }) => {
  const canWrite = useHasPermission('migration:write');
  const [name, setName] = useState('');
  const [target, setTarget] = useState(defaultTarget);
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // Which company the new import is for. null = not loaded yet.
  const [choices, setChoices] = useState<CompanyChoices | null>(null);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const [company, setCompany] = useState('');
  useEffect(() => {
    if (!canWrite) return;
    let live = true;
    Promise.all([getCreatableCompanies(), listCompanyNames()])
      .then(([reach, names]) => {
        if (!live) return;
        const c = companyChoices(reach, names);
        setChoices(c); setCompany(c.initial); setCompaniesError(null);
      })
      .catch((e) => { if (live) { setChoices(null); setCompaniesError(getApiErrorMessage(e, 'Could not load the companies you can import for.')); } });
    return () => { live = false; };
  }, [canWrite]);
  const noCompanies = !!choices && choices.companies.length === 0 && !choices.groupWide;
  const create = async () => {
    if (!company) { setCreateError('Choose which company this import is for.'); return; }
    setBusy(true); setCreateError(null);
    try { const j = await createMigration(name || 'Untitled migration', target, undefined, legalEntityIdFor(company)); setName(''); onCreated(j.id); }
    catch (e) { setCreateError(getApiErrorMessage(e, 'Could not start the migration.')); }
    finally { setBusy(false); }
  };
  const targetHint = MIGRATION_TARGETS.find((t) => t.key === target)?.hint;
  return (
    <div className="space-y-4">
      <IntelligencePanel />
      {canWrite && <Card><CardContent className="p-5 flex flex-wrap items-end gap-3">
        <label className="space-y-1.5">
          <span className="text-xs font-medium text-muted-foreground">New migration</span>
          <input className={`${fieldCls} w-64`} value={name} placeholder="e.g. Staff from old system" onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="space-y-1.5">
          <span className="text-xs font-medium text-muted-foreground">Import into</span>
          <select className={`${fieldCls} w-56`} value={target} onChange={(e) => setTarget(e.target.value)}>
            {MIGRATION_TARGETS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </label>
        <label className="space-y-1.5">
          <span className="text-xs font-medium text-muted-foreground">Company</span>
          <select aria-label="Company" className={`${fieldCls} w-56`} value={company} required disabled={!choices || noCompanies}
            onChange={(e) => { setCompany(e.target.value); setCreateError(null); }}>
            {company === '' && <option value="" disabled>{choices ? 'Choose a company…' : companiesError ? 'Companies unavailable' : 'Loading companies…'}</option>}
            {choices?.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            {choices?.groupWide && <option value={GROUP_WIDE}>All companies (group-wide)</option>}
          </select>
        </label>
        <Button onClick={create} disabled={busy || !company}>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Start migration</Button>
        {targetHint && <p className="w-full text-xs text-muted-foreground">{targetHint}</p>}
        {company === GROUP_WIDE && (
          <p className="w-full text-xs text-muted-foreground">Group-wide imports are visible only to people who can see every company.</p>
        )}
        {noCompanies && <p role="alert" className="w-full text-sm text-danger-strong">You don't have migration access to any company.</p>}
        {companiesError && <p role="alert" className="w-full text-sm text-danger-strong">{companiesError}</p>}
        {createError && <p role="alert" className="w-full text-sm text-danger-strong">{createError}</p>}
      </CardContent></Card>}

      <Card><CardContent className="p-5">
        <h2 className="text-sm font-semibold text-foreground mb-3">Recent migrations</h2>
        {listError ? <p role="alert" className="text-sm text-danger-strong">{listError}</p>
          : jobs.length === 0 ? <p className="text-sm text-muted-foreground">No migrations yet.</p> : (
          <div className="divide-y divide-border">
            {jobs.map((j) => (
              <button key={j.id} onClick={() => onOpen(j.id)} className="flex w-full items-center justify-between py-2.5 text-left hover:bg-accent rounded px-2 -mx-2">
                <div><div className="text-sm font-medium text-foreground">{j.name}
                  <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{MIGRATION_TARGETS.find((t) => t.key === j.target)?.label ?? j.target}</span>
                  <span className="ml-2"><CompanyChip id={j.legal_entity_id} names={companyNames} /></span></div>
                  <div className="text-xs text-muted-foreground">{j.total_rows} rows · {formatDate(j.created_at)}</div></div>
                <div className="flex items-center gap-3"><StatusChip s={j.status} /><ChevronRight className="h-4 w-4 text-muted-foreground" /></div>
              </button>
            ))}
          </div>
        )}
      </CardContent></Card>
    </div>
  );
};

// -------------------------------------------------------------------- job
const STEPS = ['Upload', 'Map', 'Validate', 'Go live'];
// A biometric upload answers two questions a column mapping cannot: which
// columns are the day grid, and which month those days are. They are real
// steps, so the rail says so.
const ATTENDANCE_STEPS = ['Upload', 'Format & month', 'Who is who', 'Validate', 'Go live'];
const JobView: React.FC<{ id: string; companyNames: Map<string, string>; onBack: () => void; onOpenSnapshot?: (id: string) => void }> = ({ id, companyNames, onBack, onOpenSnapshot }) => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const canWrite = useHasPermission('migration:write');
  const canApprove = useHasPermission('migration:approve');
  const setupPath = tenantSlug ? `/t/${tenantSlug}/app/admin/setup` : '/app/admin/setup';
  const [job, setJob] = useState<MigrationJob | null>(null);
  const [mappings, setMaps] = useState<Mapping[]>([]);
  const [rows, setRows] = useState<MigrationRow[]>([]);
  const [valResult, setValResult] = useState<ImportResult | null>(null);
  const [execResult, setExecResult] = useState<ImportResult | null>(null);
  const [rbResult, setRbResult] = useState<RollbackResult | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Why the job could not be shown. A job outside the caller's companies
  // answers 404 exactly like one that does not exist — say so instead of
  // spinning forever.
  const [loadFailure, setLoadFailure] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  // A ref rather than a <label>: the control is then an ordinary button that
  // can be focused, disabled and labelled, and the file input is not load
  // bearing for the layout.
  const fileInput = useRef<HTMLInputElement>(null);

  const load = async () => {
    let d: Awaited<ReturnType<typeof getMigration>>;
    try { d = await getMigration(id); }
    catch (e: any) {
      setJob(null);
      setLoadFailure(e?.response?.status === 404 ? 'Job not found.' : getApiErrorMessage(e, 'Could not load this migration.'));
      return;
    }
    setLoadFailure(null);
    setJob(d.job); setMaps(d.mappings || []);
    if (d.job.total_rows > 0) getRows(id, 5).then((r) => setRows(r.rows)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const run = async (key: string, fn: () => Promise<any>) => {
    setError(null); setBusy(key);
    try { await fn(); await load(); }
    catch (e: any) { setError(e?.response?.data?.message || e?.message || 'Something went wrong.'); }
    finally { setBusy(null); }
  };

  // A reshaping upload (a biometric day grid) has two steps of its own before
  // mapping, so it gets its own rail rather than pretending they are not
  // happening.
  const reshaping = job?.target === 'attendance';
  const steps = reshaping ? ATTENDANCE_STEPS : STEPS;
  // Warnings carried on the STAGED rows — the reshape's refusals. Distinct
  // from the importer's per-row results, which ResultView renders.
  const rowWarnings = useMemo(
    () => rows.flatMap((r) => (r.warnings ?? []).map((text) => ({ row: r.row_number, text }))),
    [rows],
  );

  const stepIdx = useMemo(() => {
    if (!job) return 0;
    if (reshaping) {
      if (job.status === 'draft') return 0;
      if (['validated'].includes(job.status)) return 3;
      if (['approved', 'executing', 'completed', 'rolled_back'].includes(job.status)) return 4;
      if (job.status === 'failed') return 3;
      return job.source_profile_id && job.period_month ? 2 : 1;
    }
    return ({ draft: 0, staged: 1, mapped: 1, validated: 2, approved: 3, executing: 3, completed: 3, rolled_back: 3, failed: 2 } as any)[job.status] ?? 0;
  }, [job, reshaping]);

  const onFile = async (file: File) => {
    if (/\.(xlsx|xlsm)$/i.test(file.name)) {
      const bytes = await file.arrayBuffer();
      run('upload', async () => { await uploadSourceBinary(id, bytes); });
      return;
    }
    const text = await file.text();
    run('upload', async () => { await uploadSource(id, text); });
  };

  if (!job && loadFailure) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> All migrations</button>
        <Card><CardContent className="p-6"><p role="alert" className="text-sm text-foreground">{loadFailure}</p></CardContent></Card>
      </div>
    );
  }
  if (!job) return <div className="flex items-center gap-2 text-muted-foreground p-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> All migrations</button>
        <StatusChip s={job.status} />
      </div>
      <div><h2 className="flex flex-wrap items-center gap-2 text-lg font-semibold text-foreground">{job.name} <CompanyChip id={job.legal_entity_id} names={companyNames} /></h2>
        <p className="text-sm text-muted-foreground">{job.total_rows} rows{job.source_headers?.length ? ` · ${job.source_headers.length} columns` : ''}
          {job.snapshot_id && onOpenSnapshot && (
            <button onClick={() => onOpenSnapshot(job.snapshot_id!)} className="ml-2 inline-flex items-center gap-1 text-primary-500 hover:underline">
              <Camera className="h-3 w-3" /> part of a cutover snapshot
            </button>
          )}</p></div>

      {/* step rail */}
      <div className="flex items-center gap-2 text-xs">
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <span className={`rounded-full px-2.5 py-1 ${i <= stepIdx ? 'bg-primary-500 text-white' : 'bg-muted text-muted-foreground'}`}>{i + 1}. {s}</span>
            {i < steps.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
          </React.Fragment>
        ))}
      </div>

      {error && <div className="rounded-md bg-danger/10 p-3 text-sm text-danger">{error}</div>}

      {/* 1. Upload or connect */}
      {job.status === 'draft' && !canWrite && (
        <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">
          Uploading source data needs the migration write permission.
        </CardContent></Card>
      )}
      {job.status === 'draft' && canWrite && (
        <Card><CardContent className="p-6 space-y-5">
          {/* The WHOLE area is the target, not a word in the middle of it.
              It used to be a <span> inside a <label> wrapping a hidden input:
              the click did reach the input, but the span's styling came from
              `bg-primary`, which is not a class this app has — `primary` is a
              colour ramp with no DEFAULT, so Tailwind emitted nothing and the
              only thing that looked clickable was the icon, which was not. */}
          <div
            role="button"
            tabIndex={0}
            aria-label="Choose a CSV file to upload"
            onClick={() => fileInput.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.current?.click(); }
            }}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault(); setDragging(false);
              const f = e.dataTransfer.files?.[0];
              if (f) onFile(f);
            }}
            className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center space-y-3 transition-colors focus:outline-none focus:ring-2 focus:ring-ring ${
              dragging ? 'border-primary-500 bg-primary-500/10' : 'border-input hover:border-primary-400'}`}
          >
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f); }}
            />
            <Upload className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden />
            <p className="text-sm text-muted-foreground">{reshaping
              ? 'Upload the attendance export exactly as the device produced it — the whole month, however many days are filled in.'
              : 'Upload a CSV of your employees. Any column names — even Swahili — Dana will map them.'}</p>
            <Button
              type="button"
              disabled={busy === 'upload'}
              onClick={(e) => { e.stopPropagation(); fileInput.current?.click(); }}
            >
              {busy === 'upload' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {busy === 'upload' ? 'Reading the file…' : 'Choose CSV'}
            </Button>
            <p className="text-xs text-muted-foreground">…or drop the file anywhere in this box.</p>
          </div>
          <ConnectSource id={id} busy={busy === 'connect'} onConnect={(connector, config) => run('connect', async () => { await connectSource(id, connector, config); })} />
        </CardContent></Card>
      )}

      {/* 1b. Biometric: which columns are the days, and which month. */}
      {reshaping && ['staged', 'mapped', 'validated', 'approved', 'failed'].includes(job.status) && (
        <AttendanceSourceCard job={job} rows={rows} canWrite={canWrite} onChanged={load} />
      )}

      {/* 1c. Biometric: who each device id is. */}
      {reshaping && job.source_profile_id && ['staged', 'mapped', 'validated', 'approved', 'completed', 'failed'].includes(job.status) && (
        <AttendanceIdentityCard job={job} canWrite={canWrite} />
      )}

      {/* 2. Map */}
      {['staged', 'mapped', 'validated', 'approved', 'completed', 'failed'].includes(job.status) && (
        <Card><CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2"><Wand2 className="h-4 w-4 text-primary-500" /> Field mapping</h3>
            <Button variant="outline" size="sm" onClick={() => run('suggest', async () => { await suggestMapping(id); })} disabled={!!busy || !canWrite}>
              {busy === 'suggest' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} {mappings.length ? 'Re-suggest with Dana' : 'Suggest with Dana'}
            </Button>
          </div>
          {reshaping && (
            <p className="text-xs text-muted-foreground">
              The day columns come from the source format above, not from here — a mapping cannot say
              “columns 4 to 33 are days 1 to 30”. Map only extra columns you want imported, such as a
              device or a branch, and continue.
            </p>
          )}
          {mappings.length === 0 ? (
            reshaping ? (
              <Button size="sm" variant="outline" onClick={() => run('savemap', async () => { await setMappings(id, mappings); })} disabled={!!busy || !canWrite}>
                {busy === 'savemap' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Nothing else to map — continue
              </Button>
            ) : <p className="text-sm text-muted-foreground">Click “Suggest with Dana” to auto-map your columns.</p>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-muted-foreground">
                  <th className="py-1.5 font-medium">Your column</th><th className="font-medium">Maps to</th>
                  <th className="font-medium">Confidence</th><th className="font-medium text-center">Import</th>
                </tr></thead>
                <tbody>
                  {mappings.map((m, i) => (
                    <tr key={m.source_field} className="border-t border-border">
                      <td className="py-1.5 font-medium text-foreground">{m.source_field}</td>
                      <td>
                        <select className={fieldCls} value={m.target_field || ''} onChange={(e) => {
                          const next = [...mappings]; next[i] = { ...m, target_field: e.target.value, approved: !!e.target.value }; setMaps(next);
                        }}>
                          <option value="">— ignore —</option>
                          {fieldsForTarget(job.target).map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                        </select>
                      </td>
                      <td>{m.target_field ? confChip(m.review_level, m.confidence) : <span className="text-xs text-muted-foreground">—</span>}</td>
                      <td className="text-center"><input type="checkbox" checked={m.approved} disabled={!m.target_field} onChange={(e) => { const next = [...mappings]; next[i] = { ...m, approved: e.target.checked }; setMaps(next); }} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center gap-3 pt-1">
                <Button size="sm" onClick={() => run('savemap', async () => { await setMappings(id, mappings); })} disabled={!!busy || !canWrite}>
                  {busy === 'savemap' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Save mapping
                </Button>
                <p className="text-xs text-muted-foreground">Only checked, mapped columns are imported. Adjust low-confidence rows. Changing mappings after approval clears the approval.</p>
              </div>
            </>
          )}
        </CardContent></Card>
      )}

      {/* preview */}
      {rows.length > 0 && (
        <Card><CardContent className="p-5">
          <h3 className="text-sm font-semibold text-foreground mb-2">Preview (first {rows.length} rows)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-muted-foreground">{job.source_headers.map((h) => <th key={h} className="py-1 pr-3 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>{rows.map((r) => <tr key={r.id} className="border-t border-border">{job.source_headers.map((h) => <td key={h} className="py-1 pr-3 whitespace-nowrap text-muted-foreground">{r.raw[h]}</td>)}</tr>)}</tbody>
            </table>
          </div>
        </CardContent></Card>
      )}

      {/* 3. Validate */}
      {['mapped', 'validated', 'approved', 'completed', 'failed'].includes(job.status) && (
        <Card><CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary-500" /> Validate (safe dry-run)</h3>
            <Button variant="outline" size="sm" onClick={() => run('validate', async () => { setValResult(await validateMigration(id)); })} disabled={!!busy || !canWrite}>
              {busy === 'validate' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />} Run validation
            </Button>
          </div>
          {valResult && <ResultView res={valResult} />}
          {/* CELLS THE SOURCE FORMAT COULD NOT READ.
              These never reach the importer — a refused cell produces no row
              — so ResultView, which only renders what the importer said, has
              nothing to show and reports "No issues". Importing a 30-column
              grid as February dropped a third of the file under exactly that
              green light. The count comes from the job; the examples come
              from the rows already loaded for the preview. */}
          {job.warning_count > 0 && (
            <div className="rounded-md bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300 space-y-1">
              <div className="flex items-start gap-2 font-medium">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                {job.warning_count} row{job.warning_count === 1 ? '' : 's'} have cells that could not be
                read and will not be imported.
              </div>
              {rowWarnings.slice(0, 5).map((w, i) => (
                <p key={i} className="pl-6 text-xs">Row {w.row}: {w.text}</p>
              ))}
              {reshaping && (
                <p className="pl-6 text-xs">
                  If these are days that do not exist, the month above is probably wrong.
                </p>
              )}
            </div>
          )}
        </CardContent></Card>
      )}

      {/* 4. Go live */}
      {['validated', 'approved', 'executing', 'completed', 'rolled_back'].includes(job.status) && (
        <Card><CardContent className="p-5 space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2"><Play className="h-4 w-4 text-primary-500" /> Go live</h3>
          {!canApprove && ['validated', 'approved'].includes(job.status) && (
            <p className="text-xs text-muted-foreground">Approving and importing needs the migration approver permission.</p>
          )}
          {job.status === 'validated' && canApprove && (
            <Button size="sm" onClick={() => run('approve', async () => { await approveMigration(id); })} disabled={!!busy}>
              {busy === 'approve' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Approve for import
            </Button>
          )}
          {(job.status === 'approved' || job.status === 'executing') && canApprove && (
            <Button size="sm" onClick={() => run('execute', async () => { setExecResult(await executeMigration(id)); })} disabled={!!busy}>
              {busy === 'execute' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />} Import now
            </Button>
          )}
          {job.status === 'completed' && (
            <>
              <div className="rounded-md bg-emerald-500/10 p-3 text-sm text-emerald-600 dark:text-emerald-300 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> Done — {job.created_count} created, {job.updated_count} updated, {job.skipped_count} skipped{job.error_count ? `, ${job.error_count} errored` : ''}{job.warning_count ? `, ${job.warning_count} with warnings` : ''}.
              </div>
              {!job.reconciled && (
                <div className="rounded-md bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-300 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" /> Counts don't reconcile — some source rows are unaccounted for. Review before treating this import as complete.
                </div>
              )}
              {ROLLBACK_COPY[job.target] && canApprove && (
                <div className="flex items-center gap-3 pt-1">
                  <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-950/30"
                    onClick={async () => { if (await confirm({ title: 'Roll back migration', message: ROLLBACK_COPY[job.target].confirm, confirmLabel: 'Roll back', danger: true })) run('rollback', async () => { setRbResult(await rollbackMigration(id)); }); }}
                    disabled={!!busy}>
                    {busy === 'rollback' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />} Roll back this import
                  </Button>
                  <span className="text-xs text-muted-foreground">{ROLLBACK_COPY[job.target].hint}</span>
                </div>
              )}
              {job.target === 'employees' && (
                <div className="pt-2">
                  <Button variant="outline" size="sm" onClick={() => navigate(setupPath)}>
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back to Getting Started
                  </Button>
                </div>
              )}
            </>
          )}
          {job.status === 'rolled_back' && (
            <div className="rounded-md bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300 flex items-center gap-2">
              <RotateCcw className="h-4 w-4" /> Rolled back{rbResult ? ` — ${rbResult.reverted} reverted, ${rbResult.restored} restored to previous values` : ''}. This import no longer affects payroll.
            </div>
          )}
          {execResult && job.status !== 'rolled_back' && <ResultView res={execResult} />}
        </CardContent></Card>
      )}
    </div>
  );
};

const CONNECTORS = [
  { value: 'google_sheets', label: 'Google Sheets', hint: 'Paste a “anyone with the link” share URL' },
  { value: 'url', label: 'Hosted CSV (URL)', hint: 'A public link to a .csv file' },
  { value: 'rest', label: 'REST API (JSON)', hint: 'An endpoint returning a JSON array of records' },
];
const ConnectSource: React.FC<{ id: string; busy: boolean; onConnect: (connector: string, config: Record<string, string>) => void }> = ({ busy, onConnect }) => {
  const [open, setOpen] = useState(false);
  const [connector, setConnector] = useState('google_sheets');
  const [url, setUrl] = useState('');
  const [auth, setAuth] = useState('');
  const meta = CONNECTORS.find((c) => c.value === connector)!;
  if (!open) {
    return <div className="text-center"><button onClick={() => setOpen(true)} className="text-sm text-primary-500 hover:underline">…or connect a source (Google Sheets, a CSV URL, or a REST API)</button></div>;
  }
  return (
    <div className="rounded-lg border border-border p-4 space-y-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="space-y-1.5"><span className="text-xs font-medium text-muted-foreground">Source</span>
          <select className={`${fieldCls} w-full`} value={connector} onChange={(e) => setConnector(e.target.value)}>
            {CONNECTORS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select></label>
        <label className="space-y-1.5 sm:col-span-2"><span className="text-xs font-medium text-muted-foreground">{meta.hint}</span>
          <input className={`${fieldCls} w-full`} value={url} placeholder="https://…" onChange={(e) => setUrl(e.target.value)} /></label>
      </div>
      {connector === 'rest' && (
        <label className="space-y-1.5 block"><span className="text-xs font-medium text-muted-foreground">Authorization header (optional)</span>
          <input className={`${fieldCls} w-full`} value={auth} placeholder="Bearer …" onChange={(e) => setAuth(e.target.value)} /></label>
      )}
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={busy || !url} onClick={() => onConnect(connector, { url, ...(auth ? { auth_header: auth } : {}) })}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Database className="mr-2 h-4 w-4" />} Connect & fetch
        </Button>
        <button onClick={() => setOpen(false)} className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
      </div>
    </div>
  );
};

const ResultView: React.FC<{ res: ImportResult }> = ({ res }) => {
  const errs = (res.results || []).filter((r) => r.errors?.length);
  const warns = (res.results || []).filter((r) => !r.errors?.length && r.warnings?.length);
  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap gap-2">
        <span className="rounded bg-emerald-500/15 px-2 py-0.5 text-emerald-600 dark:text-emerald-300">{res.summary.to_create} to create</span>
        <span className="rounded bg-blue-500/15 px-2 py-0.5 text-blue-600 dark:text-blue-300">{res.summary.to_update} to update</span>
        <span className="rounded bg-slate-500/15 px-2 py-0.5 text-slate-600 dark:text-slate-300">{res.summary.skipped} skipped</span>
        {errs.length > 0 && <span className="rounded bg-red-500/15 px-2 py-0.5 text-red-600 dark:text-red-300">{errs.length} with errors</span>}
        {warns.length > 0 && <span className="rounded bg-amber-500/15 px-2 py-0.5 text-amber-700 dark:text-amber-300">{warns.length} with warnings</span>}
      </div>
      {errs.length > 0 && (
        <div className="space-y-1">
          {errs.slice(0, 20).map((r) => (
            <div key={r.row} className="flex items-start gap-2 text-xs text-danger">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>Row {r.row}{r.email ? ` (${r.email})` : ''}: {r.errors.join('; ')}</span>
            </div>
          ))}
          {errs.length > 20 && <p className="text-xs text-muted-foreground">…and {errs.length - 20} more error rows.</p>}
        </div>
      )}
      {warns.length > 0 && (
        <div className="space-y-1">
          {warns.slice(0, 20).map((r) => (
            <div key={`w${r.row}`} className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>Row {r.row}{r.email ? ` (${r.email})` : ''}: {r.warnings.join('; ')}</span>
            </div>
          ))}
          {warns.length > 20 && <p className="text-xs text-muted-foreground">…and {warns.length - 20} more rows with warnings.</p>}
        </div>
      )}
      {errs.length === 0 && warns.length === 0 && <p className="text-xs text-muted-foreground">No issues — ready to import.</p>}
    </div>
  );
};

export default MigrationCenterPage;
